import { useState, useEffect } from 'react';
import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';
import { UserSettings } from '../../../shared/types/user';
import { apiConfig } from '../config/api';

interface UpdateSettingsInput {
  importance: UserSettings['importance'];
  subjectTemplate?: string;
  prompt: string;
}

export function useUserSettings() {
  const [userSettings, setUserSettings] = useState<UserSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchUserSettings = async () => {
    try {
      const authSession = await fetchAuthSession();
      const currentUser = await getCurrentUser();
  
      if (!authSession.tokens?.idToken) {
        throw new Error('認証トークンが見つかりません');
      }
  
      const token = authSession.tokens.idToken.toString();
      // トークンの内容を確認
      try {
        const tokenPayload = JSON.parse(atob(token.split('.')[1]));
        console.log('トークン内容:', {
          email: tokenPayload.email,
          sub: tokenPayload.sub,
          exp: new Date(tokenPayload.exp * 1000).toISOString(),
          iat: new Date(tokenPayload.iat * 1000).toISOString(),
          tokenType: tokenPayload.token_use
        });
      } catch (e) {
        console.error('トークンのデコードに失敗:', e);
      }
  
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
  
      const url = `${apiConfig.apiEndpoint}/users/${currentUser.userId}`;
      console.log('GET リクエスト詳細:', {
        url,
        method: 'GET',
        userId: currentUser.userId,
        tokenType: token.split('.')[0], // JWTのヘッダー部分
        claims: token.split('.')[1],    // JWTのペイロード部分
        headers: {
          ...headers,
          Authorization: `Bearer ${token.substring(0, 20)}...`
        }
      });
  
      const response = await fetch(url, {
        method: 'GET',
        headers
      });
  
      if (!response.ok) {
        const errorText = await response.text();
        console.error('GET レスポンス詳細:', {
          status: response.status,
          statusText: response.statusText,
          headers: Object.fromEntries(response.headers.entries()),
          body: errorText,
          // レスポンスヘッダーの詳細表示
          responseHeaders: [...response.headers].map(([key, value]) => `${key}: ${value}`).join('\n')
        });
        throw new Error(`ユーザー情報の取得に失敗しました: ${errorText}`);
      }
  
      const data = await response.json();
      setUserSettings(data);
    } catch (err) {
      console.error('エラー詳細:', {
        name: err instanceof Error ? err.name : 'Unknown Error',
        message: err instanceof Error ? err.message : String(err),
        stack: err instanceof Error ? err.stack : 'No stack trace',
        // エラーオブジェクトの完全な内容を表示
        fullError: JSON.stringify(err, null, 2)
      });
      setError(err instanceof Error ? err : new Error('不明なエラーが発生しました'));
    } finally {
      setIsLoading(false);
    }
  };

  const updateSettings = async (input: UpdateSettingsInput) => {
    try {
      if (!userSettings) throw new Error('ユーザー設定が読み込まれていません');

      const authSession = await fetchAuthSession();
      const currentUser = await getCurrentUser();
  
      if (!authSession.tokens?.idToken) {
        throw new Error('認証トークンが見つかりません');
      }
  
      const token = authSession.tokens.idToken.toString();
      const headers = {
        'Authorization': `Bearer ${token}`,  // 修正
        'Content-Type': 'application/json'
      };
  
      const url = `${apiConfig.apiEndpoint}/users/${currentUser.userId}`;
      const requestBody = JSON.stringify(input);
      console.log('PATCH リクエスト:', {
        url,
        method: 'PATCH',
        headers: {
          ...headers,
          Authorization: headers.Authorization.substring(0, 20) + '...'  // トークンを省略して表示
        },
        body: input
      });

      const response = await fetch(url, {
        method: 'PATCH',
        headers,
        body: requestBody
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('PATCH レスポンス:', {
          status: response.status,
          statusText: response.statusText,
          body: errorText
        });
        throw new Error(`設定の更新に失敗しました: ${errorText}`);
      }
  
      const data = await response.json();
      setUserSettings(data);
    } catch (err) {
      console.error('エラー詳細:', err);
      throw err;
    }
  };

  useEffect(() => {
    fetchUserSettings();
  }, []);

  return { userSettings, isLoading, error, updateSettings };
}
